import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, MessageSquare, Users, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DripCampaignManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkAction, setBulkAction] = useState({ email: false, sms: false });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/drip-campaign/leads`, { withCredentials: true });
      setLeads(response.data.leads || []);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const toggleIndividual = async (leadId, enabled, channel = 'both') => {
    try {
      const payload = {
        lead_ids: [leadId],
        enabled: enabled
      };
      
      if (channel === 'email') {
        payload.email_enabled = enabled;
      } else if (channel === 'sms') {
        payload.sms_enabled = enabled;
      } else {
        payload.email_enabled = enabled;
        payload.sms_enabled = enabled;
      }
      
      await axios.post(`${API}/admin/drip-campaign/toggle`, payload, { withCredentials: true });
      toast.success(`Drip campaign ${enabled ? 'enabled' : 'disabled'}`);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update drip campaign');
    }
  };

  const toggleBulk = async (enabled) => {
    if (selectedLeads.length === 0) {
      toast.error('Please select at least one lead');
      return;
    }

    try {
      const payload = {
        lead_ids: selectedLeads,
        enabled: enabled,
        email_enabled: bulkAction.email ? enabled : undefined,
        sms_enabled: bulkAction.sms ? enabled : undefined
      };
      
      await axios.post(`${API}/admin/drip-campaign/toggle`, payload, { withCredentials: true });
      toast.success(`Drip campaign ${enabled ? 'enabled' : 'disabled'} for ${selectedLeads.length} leads`);
      setSelectedLeads([]);
      setBulkAction({ email: false, sms: false });
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update drip campaigns');
    }
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead.id));
    }
  };

  const toggleSelect = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bulk Actions Card */}
      {selectedLeads.length > 0 && (
        <Card className="p-6 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">
                {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-3 ml-4">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={bulkAction.email}
                    onCheckedChange={(checked) => setBulkAction({ ...bulkAction, email: checked })}
                  />
                  <Mail className="w-4 h-4" />
                  Email
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={bulkAction.sms}
                    onCheckedChange={(checked) => setBulkAction({ ...bulkAction, sms: checked })}
                  />
                  <MessageSquare className="w-4 h-4" />
                  SMS
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => toggleBulk(true)}
                disabled={!bulkAction.email && !bulkAction.sms}
              >
                Enable Campaign
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => toggleBulk(false)}
                disabled={!bulkAction.email && !bulkAction.sms}
              >
                Disable Campaign
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedLeads([])}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Leads Table */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Drip Campaign Management</h3>
          <p className="text-sm text-gray-500">{leads.length} total leads</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <Checkbox
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Toggle</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map(lead => (
                <tr key={lead.id} className={selectedLeads.includes(lead.id) ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={() => toggleSelect(lead.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{lead.lead_name}</p>
                      <p className="text-xs text-gray-500">{lead.lead_email}</p>
                      <p className="text-xs text-gray-500">{lead.lead_phone}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div>
                      <p className="font-medium">{lead.property_address}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      lead.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      lead.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                      lead.status === 'contacted' ? 'bg-indigo-100 text-indigo-800' :
                      lead.status === 'closed' ? 'bg-green-100 text-green-800' :
                      lead.status === 'bad_lead' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 items-center">
                      {lead.drip_campaign_email_enabled && (
                        <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          <Mail className="w-3 h-3" />
                          Email
                        </span>
                      )}
                      {lead.drip_campaign_sms_enabled && (
                        <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          <MessageSquare className="w-3 h-3" />
                          SMS
                        </span>
                      )}
                      {!lead.drip_campaign_enabled && (
                        <span className="text-xs text-gray-400 italic">Disabled</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={lead.drip_campaign_enabled || false}
                        onCheckedChange={(checked) => toggleIndividual(lead.id, checked)}
                      />
                      <span className="text-xs text-gray-600">
                        {lead.drip_campaign_enabled ? 'On' : 'Off'}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default DripCampaignManager;
